import type {
  Common,
  SegmentEvent,
  SegmentIdentifyEvent,
  SegmentPageEvent,
  SegmentTrackEvent,
} from "@beautypie/segment"
import deepmerge from "deepmerge"
import cookie from "js-cookie"
// eslint-disable-next-line import/no-cycle
import { switchLocaleTypeToCountryType } from "data/tracking/segment/helpers"
import isbot from "isbot"
import { getInitialAnonId } from "lib/apollo/initialvals"
// eslint-disable-next-line import/no-cycle
import { getLocale } from "lib/apolloClient"
import { Logger } from "lib/logger"
// eslint-disable-next-line import/no-cycle
import { userVar } from "lib/user"
// eslint-disable-next-line import/no-cycle
import { updateLastEventTimestamp } from "session"
// eslint-disable-next-line import/no-cycle
import { getContext } from "tracking/utils/context"

type ClientEndpoint = "identify" | "page" | "track"

export const isDraftMode = (): boolean => {
  return cookie.get("draftMode") === "true"
}

const postJSON = (event, endpoint) => {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(event),
    credentials: "include",
    keepalive: true,
  }
  return fetch(
    `${process.env.NEXT_PUBLIC_BEAUTYPIE_ANALYTICS_URI}/${endpoint}`,
    requestOptions
  )
}

export const segment = async (
  endpoint: ClientEndpoint,
  event: SegmentEvent,
  requestHandler = postJSON
): Promise<void> => {
  const log = Logger(`data/tracking/segment/${endpoint}`)
  if (isbot(navigator.userAgent)) {
    return
  }

  if (process.env.NEXT_PUBLIC_DISABLE_SEGMENT_TRACKING || isDraftMode()) {
    // eslint-disable-next-line no-console
    console.log("Would have sent event to Segment: ", event)
    return
  }

  try {
    await requestHandler(event, endpoint)

    updateLastEventTimestamp()

    log.info(`Successfully posted ${endpoint} event to API`)
    return
  } catch (e) {
    log.error(`Error triggering API post request: ${JSON.stringify(e)}`)
  }
}

function userId() {
  const user = userVar()
  if (user.isLoggedIn) return user.id
}

export const getCommonFields = async (
  contextOverrides = {}
): Promise<Common> => {
  const locale = getLocale()
  const anonymousId = getInitialAnonId()
  const context = await getContext(switchLocaleTypeToCountryType(locale))

  return {
    anonymousId,
    timestamp: new Date(),
    userId: userId(),
    context: deepmerge(context || {}, contextOverrides),
  }
}

export type Track = (
  event: SegmentTrackEvent,
  contextOverrides?: Partial<Common["context"]>
) => void

export const track: Track = (event, contextOverrides): void => {
  getCommonFields(contextOverrides).then((common) =>
    segment("track", { ...event, ...common })
  )
}

export const identify = (event: SegmentIdentifyEvent): void => {
  getCommonFields().then((common) =>
    segment("identify", { ...event, ...common })
  )
}

export const page = (event: SegmentPageEvent, contextOverrides?): void => {
  getCommonFields(contextOverrides).then((common) =>
    segment("page", { ...event, ...common })
  )
}
